import React, { useEffect, useCallback, createContext, useState } from 'react';
import moment from 'moment';

import { events } from 'modules/assignment-map/events';

import * as http from 'modules/assignment-map/http';

const AssignmentMapContext = createContext();

function AssignmentMapProvider({ eventEmitter, children }) {
  const [mapCoordinates, setMapCoordinates] = useState(null);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState(null);
  const [itineraryId, setItineraryId] = useState(null);
  const [fetchingDriverPositions, setFetchingDriverPositions] = useState(true);
  const [driverPositions, setDriverPositions] = useState([]);
  const [driverSelected, setDriverSelected] = useState(null);
  const [vehicleSelected, setVehicleSelected] = useState(null);
  const [vehiclesToAssign, setVehiclesToAssign] = useState(null);
  const [positionTimestamp, setPositionTimestamp] = useState(null);
  const [itinerary, setItinerary] = useState(null);

  const { stops = [] } = itinerary || {};

  const firstStop = stops[0] || {};

  const { time_from } = firstStop;

  const fetchDriverPositions = useCallback(async (mapCoordinates) => {
    setMapCoordinates(mapCoordinates);
  }, []);

  const fetchItinerary = useCallback(async (itineraryIdToFetch) => {
    const fetchedItinerary = await http.getItinerary(itineraryIdToFetch);

    setItinerary({
      ...fetchedItinerary,
      stops: fetchedItinerary?.stops?.map((stop) => ({
        ...stop,
        latitude: stop?.address?.latitude,
        longitude: stop?.address?.longitude,
      })),
    });
  }, []);

  const onDriverMarkerClick = useCallback(({ driverId, vehicleId, vehicleIds, timestamp }) => {
    setDriverSelected(driverId);
    setVehicleSelected(vehicleId);
    setVehiclesToAssign(vehicleIds);
    setPositionTimestamp(timestamp);
  }, []);

  const closeDriverBox = useCallback(() => {
    setDriverSelected(null);
    setVehicleSelected(null);
    setVehiclesToAssign(null);
  }, []);

  const handleOpenModal = useCallback((itineraryId) => {
    setItineraryId(itineraryId);
  }, []);

  const handleCloseModal = useCallback(() => {
    setItineraryId(null);
  }, []);

  const clearItinerary = useCallback(() => {
    setItinerary(null);
    setDriverSelected(null);
  }, []);

  const clearPositions = useCallback(() => {
    setDriverPositions([]);
    setFetchingDriverPositions(true);
    setMapCoordinates(null);
  }, []);

  const filterByVehicleType = useCallback((value) => {
    setVehicleTypeFilter(value);
  }, []);

  useEffect(() => {
    const getPositions = async ({ mapCoordinates, itineraryId, time_from, vehicleTypeFilter }) => {
      const { center, northEast } = mapCoordinates;

      setFetchingDriverPositions(true);
      setDriverPositions([]);

      try {
        const positions = await http.getPositions({
          itineraryId: itineraryId,
          pickupTimeFrom: moment(time_from).format('YYYY-MM-DD'),
          center: `${center.latitude},${center.longitude}`,
          northEast: `${northEast.latitude},${northEast.longitude}`,
          vehicleTypes: (vehicleTypeFilter || []).map(({ value }) => value).join(','),
        });
        setDriverPositions(positions);
      } catch (err) {
        console.error(err);
      } finally {
        setFetchingDriverPositions(false);
      }
    };

    if (!mapCoordinates || !time_from || !itineraryId) {
      return;
    }

    getPositions({ itineraryId, time_from, mapCoordinates, vehicleTypeFilter });
  }, [mapCoordinates, itineraryId, time_from, vehicleTypeFilter]);

  useEffect(() => {
    eventEmitter.on(events.OPEN_MODAL, handleOpenModal);
    eventEmitter.on(events.CLOSE_MODAL, handleCloseModal);
    return () => {
      eventEmitter.off(events.OPEN_MODAL);
      eventEmitter.off(events.CLOSE_MODAL);
    };
  });

  return (
    <AssignmentMapContext.Provider
      value={{
        fetchItinerary,
        fetchingDriverPositions,
        driverPositions,
        onDriverMarkerClick,
        fetchDriverPositions,
        itinerary,
        driverSelected,
        vehicleSelected,
        vehiclesToAssign,
        closeDriverBox,
        itineraryId,
        clearItinerary,
        clearPositions,
        closeModal: handleCloseModal,
        vehicleTypeFilter,
        filterByVehicleType,
        positionTimestamp,
      }}
    >
      {children}
    </AssignmentMapContext.Provider>
  );
}

export { AssignmentMapProvider };
export default AssignmentMapContext;
